define([
  'axios',
  'underscore',
  'js-cookie',
  'es6!launcher/actions/DisplayRuleActions',
  'es6!launcher/util/constants',
], (
  axios, _, jscookie, Actions, Constants,
) => {
  const { LIST_BUILDER_3_APP_UUID } = Constants;

  const isNewId = (id) => !!id && id.indexOf('new-') === 0;

  function getRequestConfig() {
    return {
      withCredentials: false,
      headers: {
        'X-Sumo-Auth': jscookie.get('__smToken'),
      },
    };
  }

  function requestDisplayRules() {
    return {
      type: Actions.REQUEST_DISPLAY_RULES,
    };
  }

  function receiveDisplayRules(rules, isDisplayRulesChanged) {
    return {
      type: Actions.RECEIVE_DISPLAY_RULES,
      rules,
      isDisplayRulesChanged,
    };
  }

  function errorDisplayRules(error) {
    return {
      type: Actions.ERROR_DISPLAY_RULES,
      error,
    };
  }

  function updateDisplayRuleGroup(groupId, rules) {
    return {
      type: Actions.UPDATE_DISPLAY_RULE_GROUP,
      groupId,
      rules,
    };
  }

  function deleteDisplayRule(objectId, groupId, displayRuleId) {
    return {
      type: Actions.DELETE_DISPLAY_RULE,
      objectId,
      groupId,
      displayRuleId,
    };
  }

  function deleteDisplayRuleGroup(objectId, groupId) {
    return {
      type: Actions.DELETE_DISPLAY_RULE_GROUP,
      objectId,
      groupId,
    };
  }

  function updateDisplayRuleSetting(setting, value) {
    return {
      type: Actions.UPDATE_DISPLAY_RULE_SETTING,
      setting,
      value,
    };
  }

  function receiveDisplayRuleSets(displayRuleSets) {
    return {
      type: Actions.RECEIVE_DISPLAY_RULE_SETS,
      displayRuleSets,
    };
  }

  function receiveDisplayRuleSet(displayRuleSet) {
    return {
      type: Actions.RECEIVE_DISPLAY_RULE_SET,
      displayRuleSet,
    };
  }

  function errorDisplayRuleSet(error) {
    return {
      type: Actions.ERROR_DISPLAY_RULE_SET,
      error,
    };
  }

  function fetchDisplayRules(objectId, appId = LIST_BUILDER_3_APP_UUID) {
    return (dispatch, getState) => {
      const sumoUrl = getState().settings.get('sumoUrl');
      const siteId = getState().settings.get('siteId');

      dispatch(requestDisplayRules());

      return axios.post(`${sumoUrl}/displayrules/load`, {
        site_id: siteId,
        app_id: appId,
        object_id: objectId,
      }, getRequestConfig())

      .then((res) => {
        dispatch(receiveDisplayRules(res.data.displayRules));
        dispatch(receiveDisplayRuleSets(res.data.displayRuleSets));
      })

      .catch((error) => {
        dispatch(errorDisplayRules(error));
        console.log(error);
      });
    };
  }

  /**
   * Saves display rules attached to `currentObjectId` to the `newObjectId` if provided, if no
   * `newObjectId` is provided, `currentObjectId` will be used
   */
  function saveDisplayRules(currentObjectId, newObjectId, appId = LIST_BUILDER_3_APP_UUID) {
    return (dispatch, getState) => {
      const sumoUrl = getState().settings.get('sumoUrl');
      const siteId = getState().settings.get('siteId');
      const finalObjectId = newObjectId || currentObjectId;
      const objectDisplayRulesById = getState().displayRules.get('byObjectId').get(currentObjectId);

      if (!objectDisplayRulesById) return Promise.resolve();

      const objectDisplayRules = _.flatten(_.values(objectDisplayRulesById.toJS()));
      return axios.post(`${sumoUrl}/displayrules/saverules`, {
        site_id: siteId,
        app_id: appId,
        object_id: finalObjectId,
        display_rules: objectDisplayRules,
      }, getRequestConfig())
      .then((res) => {
        const data = res.data;

        if (!data.success) {
          return dispatch(errorDisplayRules(currentObjectId, new Error('Failed to save display rules.')));
        }

        // if we're saving new display rules, wipe out the temp placeholders
        if (isNewId(currentObjectId)) {
          _.each(objectDisplayRules, (displayRule) => {
            dispatch(deleteDisplayRule(currentObjectId, displayRule.group_id, displayRule.id));
          });
        }

        return dispatch(receiveDisplayRules(data.displayRules));
      })
      .catch((error) => {
        errorDisplayRules(error);
        console.log(error);
      });
    };
  }

  function createDisplayRuleSet(objectId, displayRuleSetName, appId = LIST_BUILDER_3_APP_UUID) {
    return (dispatch, getState) => {
      const state = getState();
      const sumoUrl = state.settings.get('sumoUrl');
      const siteId = state.settings.get('siteId');

      return axios.post(`${sumoUrl}/displayrules/createruleset`, {
        site_id: siteId,
        app_id: appId,
        name: displayRuleSetName,
        object_id: objectId,
      }, getRequestConfig())

      .then((res) => {
        const data = res.data;

        if (!data.success) {
          return dispatch(errorDisplayRuleSet(new Error('Failed to save display rule set.')));
        }

        return dispatch(receiveDisplayRuleSet(data.displayRuleSet));
      })
      .catch((err) => {
        errorDisplayRuleSet(err);
        console.log(err);
      });
    };
  }

  /**
   * Save current site display rules to an existing display rule set
   */
  function saveDisplayRuleSet(objectId, setId, appId = LIST_BUILDER_3_APP_UUID) {
    return (dispatch, getState) => {
      const sumoUrl = getState().settings.get('sumoUrl');
      const siteId = getState().settings.get('siteId');

      return axios.post(`${sumoUrl}/displayrules/saveruleset`, {
        set_id: setId,
        site_id: siteId,
        app_id: appId,
        object_id: objectId,
      }, getRequestConfig())

      .then((res) => {
        const data = res.data;

        if (!data.success) {
          return dispatch(errorDisplayRuleSet(new Error('Failed to save display rule set.')));
        }

        return Promise.resolve();
      })
      .catch((error) => {
        errorDisplayRuleSet(error);
        console.log(error);
      });
    };
  }

  function loadDisplayRuleSet(objectId, setId, appId = LIST_BUILDER_3_APP_UUID) {
    return (dispatch, getState) => {
      const sumoUrl = getState().settings.get('sumoUrl');
      const siteId = getState().settings.get('siteId');

      return axios.post(`${sumoUrl}/displayrules/loadruleset`, {
        site_id: siteId,
        app_id: appId,
        set_id: setId,
        object_id: objectId,
      }, getRequestConfig())
      .then((res) => {
        const data = res.data;

        if (!data.success) {
          return dispatch(errorDisplayRuleSet(new Error('Failed to load display rule set.')));
        }

        return dispatch(receiveDisplayRules(data.displayRules, true));
      })
      .catch((err) => {
        errorDisplayRuleSet(err);
        console.log(err);
      });
    };
  }

  function deleteDisplayRuleSet(displayRuleSet) {
    return (dispatch, getState) => {
      const sumoUrl = getState().settings.get('sumoUrl');
      const siteId = getState().settings.get('siteId');

      axios.post(`${sumoUrl}/displayrules/deleteruleset`, {
        site_id: siteId,
        set_id: displayRuleSet.id,
      }, getRequestConfig())
      .then((res) => {
        const data = res.data;

        if (!data.success) {
          return dispatch(errorDisplayRuleSet(new Error('Failed to delete display rule set.')));
        }

        return dispatch({
          type: Actions.DELETE_DISPLAY_RULE_SET,
          displayRuleSet,
        });
      });
    };
  }

  return {
    receiveDisplayRules,

    fetchDisplayRules,
    updateDisplayRuleGroup,
    updateDisplayRuleSetting,
    saveDisplayRules,
    deleteDisplayRuleGroup,
    deleteDisplayRule,

    createDisplayRuleSet,
    saveDisplayRuleSet,
    loadDisplayRuleSet,
    deleteDisplayRuleSet,
  };
});
